import type { FC } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useQuery } from "@apollo/client"

import TaigaMentionsSection from "./TaigaMentionsSection"
import styles from "./TaigaPage.module.scss"
import { type SessionStatusMap } from "./shared/sessionStatus"
import { setupEmbeddedClipboardBridge } from "./slack_mentions/utils"

import { graphql } from "@/souhl_admin/gql/__generated__"

// slack mention の embedded session の status を1本の polling でまとめて取得する。
// 子コンポーネントから node id を集約し、union を polling する。
const GET_SESSION_STATUSES = graphql(/* GraphQL */ `
	query GetSessionStatuses($param: TaigaSessionStatusesParam!) {
		taigaSessionStatuses(param: $param) {
			sessions {
				nodeId
				isDone
				session {
					threadTs
					channelId
					sessionStatus
					isRemoteControlEnabled
					memo
					ccSessionId
					claudeRemoteControlSessionId
				}
			}
		}
	}
`)

// taiga sheets 機能は利用停止したため (2026-11)、Taiga ページは Slack mentions セクションのみ表示する。
// backend (taiga_sheets / taiga_milestones・GraphQL) は残置しているが、frontend からは呼ばない。
const STORAGE_KEY = "souhl_admin.taiga.sectionVisibility"

type SectionVisibility = { mentions: boolean }

const DEFAULT_VISIBILITY: SectionVisibility = { mentions: true }

const loadVisibility = (): SectionVisibility => {
	if (typeof window === "undefined") return DEFAULT_VISIBILITY
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (!raw) return DEFAULT_VISIBILITY
		const parsed = JSON.parse(raw) as Partial<SectionVisibility>
		return {
			mentions:
				typeof parsed?.mentions === "boolean"
					? parsed.mentions
					: DEFAULT_VISIBILITY.mentions,
		}
	} catch {
		return DEFAULT_VISIBILITY
	}
}

const saveVisibility = (v: SectionVisibility) => {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(v))
	} catch {
		// localStorage 書き込み失敗時はサイレントに無視（プライベートモード等）
	}
}

const TaigaPage: FC = () => {
	const [visibility, setVisibility] =
		useState<SectionVisibility>(loadVisibility)
	const [searchParams, setSearchParams] = useSearchParams()

	// taiga-browser 拡張 (Cursor webview) の iframe 内で動いている場合、外部リンク・
	// 別タブリンクのクリックを intercept して親フレームへ postMessage し、Cursor 内の
	// 新しい webview タブで開く (デフォルトだと OS の既定ブラウザが開いてしまうため)。
	// 通常ブラウザでは受け手がおらず従来通り。
	useEffect(() => {
		if (typeof window === "undefined" || window.parent === window) return
		const handleClick = (e: MouseEvent) => {
			if (e.defaultPrevented) return
			if (!(e.target instanceof Element)) return
			const anchor = e.target.closest("a[href]")
			if (!(anchor instanceof HTMLAnchorElement)) return
			const href = anchor.href
			if (!href.startsWith("http")) return
			const isExternal = new URL(href).origin !== window.location.origin
			if (!isExternal && anchor.target !== "_blank") return
			e.preventDefault()
			window.parent.postMessage({ type: "taiga.openUrl", url: href }, "*")
		}
		// memo 内リンク等は onClick で stopPropagation しており bubble 段階では
		// document まで届かないため、capture 段階で intercept する。
		document.addEventListener("click", handleClick, true)
		return () => document.removeEventListener("click", handleClick, true)
	}, [])

	// 同じく iframe 埋め込み時のみ、Cmd+V を拡張経由の clipboard 読み取りに relay する。
	useEffect(() => setupEmbeddedClipboardBridge(), [])

	// Cursor/VS Code の webview 拡張 (taiga-browser) 内の iframe では、キー入力が
	// iframe 内で完結しエディタ側のキーバインドが一切発火しない。ユーザーの
	// キーバインド (Cmd+L: ターミナル開閉 / Cmd+1: 前のタブ / Cmd+2: 次のタブ /
	// Cmd+\: エディタを右に分割) を iframe 埋め込み時のみ捕まえて親フレームへ
	// postMessage し、拡張側で対応するコマンドを実行する。
	useEffect(() => {
		if (typeof window === "undefined" || window.parent === window) return
		const handleKeyDown = (e: KeyboardEvent) => {
			if (!e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
			const type =
				e.key === "l"
					? "taiga.toggleTerminal"
					: e.key === "1"
						? "taiga.prevEditorInGroup"
						: e.key === "2"
							? "taiga.nextEditorInGroup"
							: e.key === "\\"
								? "taiga.splitEditorRight"
								: null
			if (!type) return
			e.preventDefault()
			window.parent.postMessage({ type }, "*")
		}
		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [])

	const activeMentionUserId = searchParams.get("mentions")

	const handleActiveUserIdChange = useCallback(
		(userId: string) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev)
					next.set("mentions", userId)
					return next
				},
				{ replace: true }
			)
		},
		[setSearchParams]
	)

	const toggleMentions = useCallback(() => {
		setVisibility((prev) => {
			const next = { ...prev, mentions: !prev.mentions }
			saveVisibility(next)
			return next
		})
	}, [])

	// 子セクションが報告する session node id を集約し、3 秒間隔で polling する。
	const [mentionNodeIds, setMentionNodeIds] = useState<string[]>([])

	// 内容が同じなら再 setState しない (無駄な再レンダー / polling 再起動を防ぐ)。
	const sameIds = (a: string[], b: string[]) =>
		a.length === b.length && a.every((v, i) => v === b[i])
	const handleMentionNodeIdsChange = useCallback((ids: string[]) => {
		setMentionNodeIds((prev) => (sameIds(prev, ids) ? prev : ids))
	}, [])

	const { data: statusData, previousData: prevStatusData } = useQuery(
		GET_SESSION_STATUSES,
		{
			variables: { param: { nodeIds: mentionNodeIds } },
			fetchPolicy: "cache-and-network",
			pollInterval: mentionNodeIds.length > 0 ? 3000 : 0,
			skip: mentionNodeIds.length === 0,
			context: { fetchOptions: { skipLoading: true } },
		}
	)
	const effectiveStatusData = statusData ?? prevStatusData

	const sessionStatusMap = useMemo<SessionStatusMap>(() => {
		const map: SessionStatusMap = new Map()
		const sessions = effectiveStatusData?.taigaSessionStatuses?.sessions
		if (sessions) {
			for (const s of sessions) {
				if (s.session) {
					map.set(s.nodeId, {
						isDone: s.isDone,
						session: {
							threadTs: s.session.threadTs,
							channelId: s.session.channelId,
							sessionStatus: s.session.sessionStatus,
							isRemoteControlEnabled: s.session.isRemoteControlEnabled,
							memo: s.session.memo ?? null,
							ccSessionId: s.session.ccSessionId ?? null,
							claudeRemoteControlSessionId:
								s.session.claudeRemoteControlSessionId ?? null,
						},
					})
				}
			}
		}
		return map
	}, [effectiveStatusData])

	return (
		<div className={styles.container}>
			<TaigaMentionsSection
				isExpanded={visibility.mentions}
				onToggleExpanded={toggleMentions}
				activeUserId={activeMentionUserId}
				onActiveUserIdChange={handleActiveUserIdChange}
				sessionStatusMap={sessionStatusMap}
				onEmbeddedNodeIdsChange={handleMentionNodeIdsChange}
			/>
		</div>
	)
}

export default TaigaPage
